import { Component, inject, TemplateRef } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ActivateFtgModalComponent } from '@zero/self-service/feature-self-service/web';
import { CheckExperienceDialogNameType } from '@zero/trade/domain';
import { CheckExperienceAndFtgBase } from '@zero/trade/feature-trade/base';
import { ExperienceNoticeCryptoModalComponent } from '../experience-notice-crypto-modal/experience-notice-crypto-modal.component';
import { ExperienceNoticeModalComponent } from '../experience-notice-modal/experience-notice-modal.component';
import { NoCryptoWalletModalComponent } from '../no-crypto-wallet-modal/no-crypto-wallet-modal.component';
import { OtcNoticeModalComponent } from '../otc-notice-modal/otc-notice-modal.component';

@Component({
  selector: 'trade-check-experience-and-ftg',
  template: '',
})
export class CheckExperienceAndFtgComponent extends CheckExperienceAndFtgBase {
  readonly #ngbModal = inject(NgbModal);
  #modalRef: NgbModalRef = null;

  public override showAlertIfNecessary(): void {
    this.checkExperienceModal();
  }

  public override dialogClosed(): void {
    this.#modalRef.close('success');
    this.#modalRef = null;
  }

  public async checkExperienceModal(): Promise<void> {
    if (this.shouldOpenModal()) {
      const { modalContent, showExperienceNotice } = this.defineModalContent();
      if (modalContent) {
        await this.openModal(modalContent, showExperienceNotice);
      }
    }
  }

  private shouldOpenModal(): boolean {
    return !this.#modalRef && !this.allowedToProceedOrder && !!this.assetToCheck;
  }

  private defineModalContent(): { modalContent: TemplateRef<HTMLElement> | null; showExperienceNotice: boolean } {
    const modalMap: Record<CheckExperienceDialogNameType, any> = {
      ftgNotice: ActivateFtgModalComponent,
      noWalletWarning: NoCryptoWalletModalComponent,
      otcNotice: OtcNoticeModalComponent,
      experienceNotice: ExperienceNoticeModalComponent,
      experienceNoticeKrypto: ExperienceNoticeCryptoModalComponent,
    };

    for (const key in modalMap) {
      if (this.dialogsToShow[key]) {
        return {
          modalContent: modalMap[key],
          showExperienceNotice: key === 'ftgNotice' && this.dialogsToShow['experienceNotice'],
        };
      }
    }

    return { modalContent: null, showExperienceNotice: false };
  }

  private async openModal(modalContent: TemplateRef<HTMLElement>, showExperienceNotice: boolean): Promise<void> {
    this.#modalRef = this.#ngbModal.open(modalContent, { size: 'lg', centered: true });

    if ('componentInstance' in this.#modalRef) {
      const instance = this.#modalRef.componentInstance;
      instance.showExperienceNotice = showExperienceNotice;
      instance.dialogMode = this.dialogMode;
      instance.showCancelButton = this.showCancelButton;
    }

    try {
      const result = await this.#modalRef.result;
      this.handleModalResult(result);
    } catch {
      this.experienceCheckCanceled.emit();
    } finally {
      this.#modalRef = null;
    }
  }

  private handleModalResult(result: string): void {
    if (result === 'otcNotice') {
      this.proceedAndConfirmOtc();
    } else if (result in this.dialogsToShow) {
      this.proceed(result as CheckExperienceDialogNameType);
    } else {
      this.experienceCheckCanceled.emit();
    }
  }
}
